import { useState, memo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSpring, animated } from '@react-spring/web'
import microApp from '@micro-zoe/micro-app'
import { Input, Button, Icon, Dropdown } from 'mk-pc'
import { MessageTip } from '@/components'
import { useAppNavigate, useUser } from '@mkmp/core'
import { useAuth } from '@/hooks'
import DefaultHeader from '@/assets/images/default-header.png'
import './index.scss'

/**
 * 布局页头部
 */
export const LayoutHeader = memo(({ isOpen }: { isOpen: boolean }) => {
  const navigate = useAppNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useUser()
  const { userLogout } = useAuth()
  const [keyword, setKeyword] = useState('')

  const [heightStyles, heightApi] = useSpring(() => {
    return {
      from: {
        height: 0
      },
      config: {
        duration: 200
      }
    }
  })

  const [displayStyles, displayApi] = useSpring(() => {
    return {
      from: {
        opacity: 0
      },
      config: {
        duration: 500
      }
    }
  })

  useEffect(() => {
    heightApi.start({
      to: {
        height: isOpen ? 56 : 0
      }
    })

    displayApi.start({
      to: {
        opacity: isOpen ? 1 : 0
      }
    })
  }, [isOpen])

  useEffect(() => {
    setKeyword(searchParams.get('keyword') || '')
  }, [searchParams])

  useEffect(() => {
    function dataListener(data: any) {
      if (data.clearKeyword) {
        setKeyword('')
      }
    }

    microApp.addGlobalDataListener(dataListener)

    return () => {
      // 解绑监听函数
      microApp.removeGlobalDataListener(dataListener)
    }
  }, [])

  /**
   * 退出
   */
  const handleLogout = async () => {
    await userLogout()

    window.location.replace('/login')
  }

  /**
   * 监听搜索内容变化
   * @param e
   */
  const onKeywordChange = (e: any) => {
    setKeyword(e.target.value)
    if (!e.target.value) {
      toSearch(e.target.value)
    }
  }

  /**
   * 搜索
   * @returns
   */
  const handleSearch = () => {
    if (!keyword) return

    toSearch(keyword)
  }

  /**
   * 搜索调整
   */
  const toSearch = (keyword: string) => {
    const { pathname } = window.location

    let url = ''
    if (['/kbs/cloud/plan', '/kbs/cloud/picture', '/kbs/cloud/video', '/kbs/cloud/other'].includes(pathname)) {
      url = pathname.replace('/kbs', '')
    } else {
      url = '/cloud/plan'
    }

    if (keyword) {
      url = url + `?keyword=${encodeURI(keyword)}`
    }

    navigate(url, {
      appName: 'kbsApp',
      appBaseRoute: '/kbs/'
    })
  }

  /**
   * 消息中心
   */
  const toMessage = () => {
    navigate('/message', { appName: 'osApp', mainApp: true })
  }

  /**
   * 去上传分享
   */
  const toUploader = () => {
    navigate('/uploader', { appName: 'kbsApp', appBaseRoute: '/kbs/' })
  }

  /**
   * 到主页
   */
  const toHome = () => {
    navigate('/cloud/plan', { appName: 'kbsApp', appBaseRoute: '/kbs/' })
  }

  /**
   * 跳转个人信息
   */
  const toUserCenter = () => {
    navigate('/user/info', { appName: 'osApp', mainApp: true })
  }

  /**
   * 跳转个人设置
   */
  const toMineCloud = () => {
    navigate('/mine/uploader', { appName: 'kbsApp', appBaseRoute: '/kbs/' })
  }

  return (
    <animated.header className="layout-header" style={{ ...heightStyles, ...displayStyles }}>
      {isOpen && (
        <>
          <div className="layout-header__logo" onClick={toHome}>
            <span className="logo"></span>
          </div>
          <div className="layout-header__search">
            <Input
              value={keyword}
              className="search-input"
              placeholder="请输入查询内容"
              allowClear
              onPressEnter={handleSearch}
              onChange={onKeywordChange}
            />
            <Button className="search-button" onClick={handleSearch}>
              美洋一下
            </Button>
          </div>
          <div className="right">
            <Button className="share-btn" onClick={toUploader}>
              <Icon name="icon-fenxiang" />
              我要分享
            </Button>
            <ul className="btns">
              <li onClick={toMessage}>
                <MessageTip />
              </li>
            </ul>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'userUploader',
                    label: (
                      <div className="user-menu-item" onClick={toMineCloud}>
                        我的空间
                      </div>
                    )
                  },
                  {
                    key: 'userCenter',
                    label: (
                      <div className="user-menu-item" onClick={toUserCenter}>
                        个人中心
                      </div>
                    )
                  },
                  {
                    key: 'logout',
                    label: (
                      <div className="user-menu-item" onClick={handleLogout}>
                        退出登录
                      </div>
                    )
                  }
                ]
              }}
              overlayClassName="layout-header__menu"
            >
              <div className="user">
                <div className="avatar">
                  <img src={DefaultHeader} />
                </div>
                <span className="name">{user?.name}</span>
              </div>
            </Dropdown>
          </div>
        </>
      )}
    </animated.header>
  )
})
